import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Download, Building2, Calendar, FileText, Loader2, AlertCircle, 
  CheckCircle, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brand logos
const BRAND_LOGOS = {
  brandstage: "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png",
  continex: "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/48hsc5ur_logo%202.png",
  tradevesta: "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/56cp3jbh_image.png"
};

export default function PublicInvoice() {
  const { invoiceId, token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  
  useEffect(() => {
    fetchInvoice();
  }, [invoiceId, token]);
  
  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/invoices/public/${invoiceId}/${token}`);
      setInvoice(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('This invoice link is invalid or has expired.');
      } else if (err.response?.status === 404) {
        setError('Invoice not found.');
      } else {
        setError('Failed to load invoice. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/invoices/public/${invoiceId}/${token}/pdf`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice?.invoice_number || 'invoice'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Cannot Access Invoice</h2>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Home
          </Link>
        </div>
      </div>
    );
  }
  
  if (!invoice) return null;
  
  const brandConfig = invoice.brand_config || {};
  const client = invoice.client || {};
  const bankDetails = invoice.bank_details || {};
  const totals = invoice.totals || {};
  const lineItems = invoice.line_items || [];
  
  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Action Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-[#C41E3A]" />
            <div>
              <p className="font-bold text-gray-900">Invoice {invoice.invoice_number}</p>
              <p className="text-sm text-gray-500">{client.company_name}</p>
            </div>
          </div>
          <button
            onClick={downloadPDF}
            disabled={downloading}
            className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-5 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download PDF
          </button>
        </div>
        
        {/* Invoice Document */}
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          {/* Header */}
          <div className="flex justify-between items-start mb-8 pb-6 border-b border-gray-200">
            <img 
              src={BRAND_LOGOS[invoice.brand]} 
              alt={invoice.brand}
              className="h-16 object-contain"
            />
            <div className="text-right text-sm">
              <p className="font-bold text-gray-900">{brandConfig.name}</p>
              <p className="text-gray-600 max-w-xs">{brandConfig.address}</p>
              <p className="text-gray-600 mt-1">TIN: {brandConfig.tin}</p>
              <p className="text-gray-600">GSTIN: {brandConfig.gst}</p>
            </div>
          </div>
          
          {/* Invoice Title & Info */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">INVOICE</h1>
              <p className="text-gray-500">Bill No: <span className="font-semibold text-gray-900">{invoice.invoice_number}</span></p>
            </div>
            <div className="text-right">
              <p className="text-gray-500">Bill Date</p>
              <p className="font-semibold text-gray-900">{invoice.invoice_date}</p>
            </div>
          </div>
          
          {/* Client Info */}
          <div className="bg-gray-50 rounded-lg p-5 mb-8">
            <p className="text-sm text-gray-500 mb-1">Issued to:</p>
            <p className="font-bold text-lg text-gray-900">{client.company_name}</p>
            {client.address && <p className="text-gray-700">{client.address}</p>}
            {client.contact_person && (
              <p className="text-gray-700">
                K/A: {client.contact_person}
                {client.contact_designation && ` (${client.contact_designation})`}
              </p>
            )}
            {client.gstin && <p className="text-gray-600 text-sm mt-1">GSTIN: {client.gstin}</p>}
          </div>
          
          {/* Purpose */}
          <div className="mb-6">
            <p className="text-gray-500 text-sm">Description of services:</p>
            <p className="font-medium text-gray-900">{invoice.purpose}</p>
          </div>
          
          {/* Line Items Table */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-800 text-white">
                  <th className="text-left py-3 px-4 font-semibold">Description of Charges</th>
                  <th className="text-right py-3 px-4 font-semibold">INR</th>
                  {invoice.foreign_currency && (
                    <th className="text-right py-3 px-4 font-semibold">{invoice.foreign_currency}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, idx) => {
                  const amountInr = (item.quantity || 1) * (item.rate_inr || 0);
                  const amountForeign = invoice.exchange_rate ? amountInr / invoice.exchange_rate : null;
                  
                  return (
                    <tr key={idx} className="border-b border-gray-200">
                      <td className="py-3 px-4 text-gray-700">{item.description}</td>
                      <td className="py-3 px-4 text-right text-gray-900">₹{amountInr.toLocaleString()}</td>
                      {invoice.foreign_currency && (
                        <td className="py-3 px-4 text-right text-gray-900">{amountForeign?.toFixed(2)}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {invoice.include_gst && (
                  <>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-4 text-gray-600">Subtotal</td>
                      <td className="py-2 px-4 text-right text-gray-900">₹{totals.subtotal_inr?.toLocaleString()}</td>
                      {invoice.foreign_currency && (
                        <td className="py-2 px-4 text-right text-gray-900">{totals.subtotal_foreign?.toFixed(2)}</td>
                      )}
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-4 text-gray-600">GST ({invoice.gst_rate || 18}%)</td>
                      <td className="py-2 px-4 text-right text-gray-900">₹{totals.gst_amount_inr?.toLocaleString()}</td>
                      {invoice.foreign_currency && (
                        <td className="py-2 px-4 text-right text-gray-900">{totals.gst_amount_foreign?.toFixed(2)}</td>
                      )}
                    </tr>
                  </>
                )}
                <tr className="bg-gray-100 font-bold">
                  <td className="py-3 px-4 text-gray-900">Grand Total</td>
                  <td className="py-3 px-4 text-right text-gray-900">₹{totals.grand_total_inr?.toLocaleString()}</td>
                  {invoice.foreign_currency && (
                    <td className="py-3 px-4 text-right text-gray-900">
                      {invoice.foreign_currency} {totals.grand_total_foreign?.toLocaleString()}
                    </td>
                  )}
                </tr>
              </tfoot>
            </table>
          </div>
          
          {/* Amount in Words */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-500">Amount in words:</p>
            <p className="font-medium text-gray-900">{invoice.amount_in_words}</p>
          </div>
          
          {/* Bank Details */}
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 mb-3">Bank Details:</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <p>Account No: <span className="font-medium">{bankDetails.account_number}</span></p>
              <p>Bank: <span className="font-medium">{bankDetails.bank_name}</span></p>
              <p>Branch: <span className="font-medium">{bankDetails.branch_address}</span></p>
              <p>Account Holder: <span className="font-medium">{bankDetails.account_holder}</span></p>
              {bankDetails.swift_code && (
                <p>SWIFT Code: <span className="font-medium">{bankDetails.swift_code}</span></p>
              )}
              {bankDetails.sort_code && (
                <p>Sort Code: <span className="font-medium">{bankDetails.sort_code}</span></p>
              )}
              {bankDetails.other_details && (
                <p>{bankDetails.other_details}</p>
              )}
            </div>
          </div>
          
          {/* Exchange Rate Note */}
          {invoice.exchange_rate && (
            <p className="text-sm text-gray-500 mb-4">
              * Conversion rate as per {invoice.exchange_rate_source || 'OANDA'}: 
              1 {invoice.foreign_currency} = ₹{invoice.exchange_rate}
            </p>
          )}
          
          {/* Relationship Note (for BrandStage/TradeVesta) */}
          {brandConfig.relationship_note && (
            <p className="text-sm text-gray-500 italic mb-6">
              {brandConfig.relationship_note}
            </p>
          )}
          
          {/* Footer */}
          <div className="flex justify-between items-end pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">E. & O. E.</p>
            <div className="text-right">
              <p className="text-sm text-gray-600 mb-8">Authorised Signatory</p>
              <p className="text-sm text-gray-500">(Signature affixed)</p>
            </div>
          </div>
        </div>
        
        {/* Footer Note */}
        <p className="text-center text-gray-500 text-sm mt-6">
          This is a computer-generated invoice. Powered by BrandStage.
        </p>
      </div>
    </div>
  );
}
